import { useTranslation } from 'react-i18next';
import { useRef, useState, useEffect, useCallback, memo } from 'react';
import { useFrame, ThreeEvent, useThree } from '@react-three/fiber';

import { Text, Edges } from '@react-three/drei';
import * as THREE from 'three';
import { useAppStore } from '../../state/store';
import { useBIMStore } from '../../state/useBIMStore';
import { computeElementsBBox } from '../../services/linked/LinkedProjectService';
import { useContextMenuStore } from '../../state/useContextMenuStore';
import { useCommandManager } from '../../state/commandStore';
import { appAlert } from '../../lib/dialogs';

const HOVER_COLOR = '#b3d9ff';
const NORMAL_COLOR = '#f0f0f0';
const TEXT_COLOR = '#333333';
// Dark silhouette outline: without it the flat unlit white faces vanish
// against light backgrounds (plan view renders white paper-style content).
const EDGE_COLOR = '#333333';
// Light internal division lines: the subtle 3x3 grid between the pieces.
// Kept much lighter than the silhouette so each face reads as one clean
// surface at small HUD sizes instead of a dense outlined mosaic.
const EDGE_INNER_COLOR = '#c9c9c9';

const ANIMATION_DURATION = 500;

type ViewPoint = 'TOP' | 'BOTTOM' | 'FRONT' | 'BACK' | 'LEFT' | 'RIGHT' |
    'TOP_FRONT' | 'TOP_BACK' | 'TOP_LEFT' | 'TOP_RIGHT' |
    'BOTTOM_FRONT' | 'BOTTOM_BACK' | 'BOTTOM_LEFT' | 'BOTTOM_RIGHT' |
    'TOP_FRONT_LEFT' | 'TOP_FRONT_RIGHT' | 'TOP_BACK_LEFT' | 'TOP_BACK_RIGHT' |
    'BOTTOM_FRONT_LEFT' | 'BOTTOM_FRONT_RIGHT' | 'BOTTOM_BACK_LEFT' | 'BOTTOM_BACK_RIGHT';

// Face labels lie ON their face (classic ViewCube style): each label is
// rotated into its face plane, so whichever face points at the camera shows
// its label face-on and readable. Offsets are relative to the part center
// (half slab thickness + epsilon keeps the text just above the surface).
const FACE_LABELS: Partial<Record<ViewPoint, { offset: [number, number, number]; rotation: [number, number, number] }>> = {
    TOP: { offset: [0, 0, 0.06], rotation: [0, 0, 0] },
    BOTTOM: { offset: [0, 0, -0.06], rotation: [0, Math.PI, 0] },
    FRONT: { offset: [0, -0.06, 0], rotation: [Math.PI / 2, 0, 0] },
    BACK: { offset: [0, 0.06, 0], rotation: [-Math.PI / 2, 0, Math.PI] },
    LEFT: { offset: [-0.06, 0, 0], rotation: [Math.PI / 2, -Math.PI / 2, 0] },
    RIGHT: { offset: [0.06, 0, 0], rotation: [Math.PI / 2, Math.PI / 2, 0] },
};

interface CubePartProps {
    position: [number, number, number];
    rotation?: [number, number, number];
    scale?: [number, number, number];
    name: ViewPoint;
    label?: string;
    onHover: (name: string | null) => void;
    onClick: (name: ViewPoint) => void;
    hovered: boolean;
    type: 'face' | 'edge' | 'corner';
    opacity: number;
}

// Flush shell construction: every piece's outer surface lies on the same
// cube boundary (half-size R), so nothing protrudes. C is the corner/edge
// cell size (classic ViewCube proportion: 1/5 of the face width) and D the
// slab thickness; with C == D every piece shares one uniform shell depth.
const R = 0.5;
const C = 0.1;
const D = 0.1;
const PANEL_SIZE = 2 * (R - C); // center panel width / edge piece length
const FACE_OFFSET = R - D / 2;  // face panel center offset
const CELL_OFFSET = R - C / 2;  // edge & corner center offset

// Division grid + silhouette lines. Per-piece outlines used to stack
// coincident segments on every shared boundary (line on line, line on
// face), so fragments visibly fought over depth along the cube edges.
// Each segment now exists exactly once and is lifted a hair off the shell
// surfaces: grid lines along their face normal, silhouette edges along the
// outward diagonal of the two faces they join.
const LINE_OFFSET = 0.004;

const buildCubeLines = (): { grid: Float32Array; silhouette: Float32Array } => {
    const g = R - C; // grid division offset from the cube center
    const grid: number[] = [];
    const silhouette: number[] = [];
    const add = (arr: number[], p: number[], q: number[]) => arr.push(...p, ...q);

    for (const axis of [0, 1, 2]) {
        const [u, v] = [0, 1, 2].filter((a) => a !== axis);
        const at = (w: number, pu: number, pv: number): number[] => {
            const p = [0, 0, 0];
            p[axis] = w;
            p[u] = pu;
            p[v] = pv;
            return p;
        };
        const lift = (p: number[], dir: number[]): number[] =>
            p.map((c, i) => c + dir[i] * LINE_OFFSET);

        for (const sign of [-1, 1]) {
            const normal = [0, 0, 0];
            normal[axis] = sign;
            // 4 division lines per face
            for (const s of [-g, g]) {
                add(grid, lift(at(sign * R, s, -R), normal), lift(at(sign * R, s, R), normal));
                add(grid, lift(at(sign * R, -R, s), normal), lift(at(sign * R, R, s), normal));
            }
        }

        // 4 silhouette edges per axis direction, pushed out diagonally so
        // they sit clear of both meeting faces
        const k = LINE_OFFSET * Math.SQRT1_2;
        for (const su of [-1, 1]) {
            for (const sv of [-1, 1]) {
                const diagonal = [0, 0, 0];
                diagonal[u] = su * k;
                diagonal[v] = sv * k;
                add(silhouette, lift(at(-R, su * R, sv * R), diagonal), lift(at(R, su * R, sv * R), diagonal));
            }
        }
    }

    return { grid: new Float32Array(grid), silhouette: new Float32Array(silhouette) };
};

const CUBE_LINES = buildCubeLines();

const GRID_GEOMETRY = new THREE.BufferGeometry();
GRID_GEOMETRY.setAttribute('position', new THREE.BufferAttribute(CUBE_LINES.grid, 3));
const SILHOUETTE_GEOMETRY = new THREE.BufferGeometry();
SILHOUETTE_GEOMETRY.setAttribute('position', new THREE.BufferAttribute(CUBE_LINES.silhouette, 3));

const Part = ({ position, rotation, scale, name, label, onHover, onClick, hovered, opacity }: CubePartProps) => {
    const color = hovered ? HOVER_COLOR : NORMAL_COLOR;
    // Adjust opacity for parts
    const partOpacity = hovered ? Math.min(1, opacity + 0.2) : opacity;
    const labelPlacement = label ? FACE_LABELS[name] : undefined;

    return (
        <group position={position} rotation={rotation ? new THREE.Euler(...rotation) : undefined}>
            <mesh
                onPointerOver={(e) => { e.stopPropagation(); onHover(name); }}
                onPointerOut={(e) => { e.stopPropagation(); onHover(null); }}
                onPointerDown={(e) => { e.stopPropagation(); }}
                onClick={(e) => { e.stopPropagation(); onClick(name); }}
            >
                <boxGeometry args={scale} />
                <meshBasicMaterial color={color} transparent opacity={partOpacity} />
            </mesh>
            {labelPlacement && (
                <Text
                    position={labelPlacement.offset}
                    rotation={labelPlacement.rotation}
                    fontSize={0.18}
                    color={TEXT_COLOR}
                    anchorX="center"
                    anchorY="middle"
                    fillOpacity={opacity} // Fade text too
                >
                    {label}
                </Text>
            )}
        </group>
    );
};

const HomeButton = ({ onClick, hovered, onHover, opacity }: { onClick: () => void, hovered: boolean, onHover: (v: boolean) => void, opacity: number }) => {
    const btnOpacity = hovered ? Math.min(1, opacity + 0.2) : opacity;
    return (
        <group position={[0.8, 0.8, 0.8]} onClick={(e) => { e.stopPropagation(); onClick(); }}
            onPointerDown={(e) => { e.stopPropagation(); }}
            onPointerOver={(e) => { e.stopPropagation(); onHover(true); }}
            onPointerOut={(e) => { e.stopPropagation(); onHover(false); }}
        >
            <mesh>
                <boxGeometry args={[0.3, 0.3, 0.3]} />
                <meshBasicMaterial color={hovered ? HOVER_COLOR : '#ffffff'} transparent opacity={btnOpacity} />
                <Edges lineWidth={1} color={EDGE_COLOR} />
            </mesh>
            <Text position={[0, 0, 0.16]} fontSize={0.2} color="black" fillOpacity={btnOpacity}>H</Text>
        </group>
    );
};

const WorkbenchViewCubeComponent = ({ mainCamera, mainControls }: { mainCamera: THREE.Camera, mainControls: any }) => {
    const { t } = useTranslation();
    const viewCubeOptions = useAppStore((state) => state.viewCubeOptions);

    // Size Logic: the options slider stores a percent of the default scale
    // (50-150, 100 = normal). Clamp defensively so an out-of-range persisted
    // value can't blow the cube up or shrink it into nothing.
    const sizePercent = Number.isFinite(viewCubeOptions.size) ? viewCubeOptions.size : 100;
    const baseScale = 30 * Math.min(1.5, Math.max(0.5, sizePercent / 100));

    // Opacity Logic. The options slider is "inactive opacity"; clamp the
    // effective value so the cube can never render fully invisible while
    // staying interactive (hiding it entirely is the showViewCube toggle's
    // job). NaN-safe: a missing/invalid value falls back to fully opaque.
    const opacitySetting = Number.isFinite(viewCubeOptions.opacity)
        ? viewCubeOptions.opacity / 100
        : 1;
    const opacity = Math.max(0.25, opacitySetting);
    const [hoveredPart, setHoveredPart] = useState<string | null>(null);
    const [homeHovered, setHomeHovered] = useState(false);

    const updateView = useAppStore((state) => state.updateView);
    // Store write dedupe: setViewCubeHovered fires on every pointer-over/out of
    // any cube part; skipping identical writes avoids redundant invalidations.
    const lastHoveredRef = useRef<boolean | null>(null);
    const setViewCubeHoveredIfChanged = useCallback((hovered: boolean) => {
        if (lastHoveredRef.current === hovered) return;
        lastHoveredRef.current = hovered;
        useAppStore.getState().setViewCubeHovered(hovered);
    }, []);
    // Action-only read via selector: zustand actions keep a stable identity,
    // so this never re-renders (was a whole-store subscription).
    const show = useContextMenuStore((s) => s.show);
    const registerCommand = useCommandManager((state) => state.registerCommand);
    const unregisterCommand = useCommandManager((state) => state.unregisterCommand);

    // Animation refs
    const targetQuaternion = useRef<THREE.Quaternion | null>(null);
    const targetPosition = useRef<THREE.Vector3 | null>(null);
    const animationStartTime = useRef<number>(0);
    const startQuaternion = useRef<THREE.Quaternion>(new THREE.Quaternion());
    const startPosition = useRef<THREE.Vector3>(new THREE.Vector3());

    // Drag State
    const isDragging = useRef(false);
    const previousMousePosition = useRef({ x: 0, y: 0 });

    const handleHover = (name: string | null) => {
        setHoveredPart(name);
        document.body.style.cursor = name ? 'pointer' : 'auto';
        setViewCubeHoveredIfChanged(!!name);
    };

    const animateCamera = useCallback((newPos: THREE.Vector3, newUp: THREE.Vector3, target: THREE.Vector3) => {
        if (!mainCamera || !mainControls) return;

        startPosition.current.copy(mainCamera.position);
        startQuaternion.current.copy(mainCamera.quaternion);
        targetPosition.current = newPos;

        const dummyCam = mainCamera.clone();
        dummyCam.position.copy(newPos);
        dummyCam.up.copy(newUp);
        dummyCam.lookAt(target);
        targetQuaternion.current = dummyCam.quaternion;

        animationStartTime.current = performance.now();
    }, [mainCamera, mainControls]);

    const handleClick = useCallback((viewName: ViewPoint) => {
        if (!mainControls || !mainCamera) return;

        const offset = mainCamera.position.distanceTo(mainControls.target);
        const target = mainControls.target.clone();
        const pos = new THREE.Vector3();
        const up = new THREE.Vector3(0, 0, 1);

        switch (viewName) {
            case 'TOP': pos.set(0, 0, offset); up.set(0, 1, 0); break;
            case 'BOTTOM': pos.set(0, 0, -offset); up.set(0, 1, 0); break;
            case 'FRONT': pos.set(0, -offset, 0); break;
            case 'BACK': pos.set(0, offset, 0); break;
            case 'LEFT': pos.set(-offset, 0, 0); break;
            case 'RIGHT': pos.set(offset, 0, 0); break;

            case 'TOP_FRONT_RIGHT': pos.set(1, -1, 1).normalize().multiplyScalar(offset); break;
            case 'TOP_FRONT_LEFT': pos.set(-1, -1, 1).normalize().multiplyScalar(offset); break;
            case 'TOP_BACK_RIGHT': pos.set(1, 1, 1).normalize().multiplyScalar(offset); break;
            case 'TOP_BACK_LEFT': pos.set(-1, 1, 1).normalize().multiplyScalar(offset); break;

            case 'TOP_FRONT': pos.set(0, -1, 1).normalize().multiplyScalar(offset); break;
            case 'TOP_BACK': pos.set(0, 1, 1).normalize().multiplyScalar(offset); break;
            case 'TOP_LEFT': pos.set(-1, 0, 1).normalize().multiplyScalar(offset); break;
            case 'TOP_RIGHT': pos.set(1, 0, 1).normalize().multiplyScalar(offset); break;

            case 'BOTTOM_FRONT_RIGHT': pos.set(1, -1, -1).normalize().multiplyScalar(offset); break;
            case 'BOTTOM_FRONT_LEFT': pos.set(-1, -1, -1).normalize().multiplyScalar(offset); break;
            case 'BOTTOM_BACK_RIGHT': pos.set(1, 1, -1).normalize().multiplyScalar(offset); break;
            case 'BOTTOM_BACK_LEFT': pos.set(-1, 1, -1).normalize().multiplyScalar(offset); break;

            case 'BOTTOM_FRONT': pos.set(0, -1, -1).normalize().multiplyScalar(offset); break;
            case 'BOTTOM_BACK': pos.set(0, 1, -1).normalize().multiplyScalar(offset); break;
            case 'BOTTOM_LEFT': pos.set(-1, 0, -1).normalize().multiplyScalar(offset); break;
            case 'BOTTOM_RIGHT': pos.set(1, 0, -1).normalize().multiplyScalar(offset); break;

            default: return;
        }

        animateCamera(target.clone().add(pos), up, target);
    }, [mainCamera, mainControls, animateCamera]);

    const handleHome = useCallback(() => {
        if (!mainControls || !mainCamera) return;

        const homeView = useAppStore.getState().homeView;

        // Use the home view from store
        const target = new THREE.Vector3(...homeView.camera.target);
        const pos = new THREE.Vector3(...homeView.camera.position);
        const up = new THREE.Vector3(...homeView.camera.up);

        animateCamera(pos, up, target);
    }, [mainCamera, mainControls, animateCamera]);

    const handleContextMenu = (e: ThreeEvent<MouseEvent>) => {
        e.stopPropagation();
        const nativeEvent = e.nativeEvent;
        nativeEvent.preventDefault();
        nativeEvent.stopPropagation();
        show(nativeEvent.clientX, nativeEvent.clientY, 'viewcube');
    };

    // Register ViewCube Commands
    useEffect(() => {
        registerCommand({
            id: 'viewCubeGoHome',
            label: 'Go Home',
            category: 'View',
            action: handleHome,
            description: 'Restores the default 3D view.'
        });

        registerCommand({
            id: 'viewCubeToggleProjection',
            label: 'Toggle Projection',
            category: 'View',
            action: () => {
                // Access store directly to avoid dependency on view.projection
                const currentProjection = useAppStore.getState().view.projection;
                updateView({ projection: currentProjection === 'orthographic' ? 'perspective' : 'orthographic' });
            },
            description: 'Toggles between Orthographic and Perspective projection.'
        });

        registerCommand({
            id: 'viewCubeSaveViewAsHome',
            label: 'Save View as Home',
            category: 'View',
            action: () => {
                if (mainCamera && mainControls) {
                    const currentView = useAppStore.getState().view;
                    const newHomeView = {
                        ...currentView,
                        camera: {
                            ...currentView.camera,
                            position: mainCamera.position.toArray(),
                            target: mainControls.target.toArray(),
                            up: mainCamera.up.toArray(),
                        }
                    };

                    useAppStore.getState().setHomeView(newHomeView);
                    console.log('Current view saved as Home');
                }
            },
            description: 'Saves the current view as the Home view.'
        });

        registerCommand({
            id: 'viewCubeLockToSelection',
            label: 'Lock to Selection',
            category: 'View',
            action: async () => {
                const selectedIds = useAppStore.getState().selectedIds;
                if (selectedIds.length === 0) {
                    await appAlert('Please select an element first.', { title: 'Lock to Selection' });
                    return;
                }

                // Real selection extents: quick point fields first, then the
                // generic geometry walker over the BIM store for everything
                // else (walls, slabs, MEP runs, components...).
                const entities = useAppStore.getState().entities;
                const bounds = new THREE.Box3();
                let hasBounds = false;

                selectedIds.forEach(id => {
                    const entity = entities[id];
                    if (entity?.data?.position) {
                        const p = entity.data.position;
                        bounds.expandByPoint(new THREE.Vector3(p.x || p[0], p.y || p[1], p.z || p[2]));
                        hasBounds = true;
                    } else if (entity?.data?.start && entity?.data?.end) {
                        const s = entity.data.start;
                        const e = entity.data.end;
                        bounds.expandByPoint(new THREE.Vector3(s.x || s[0], s.y || s[1], s.z || s[2]));
                        bounds.expandByPoint(new THREE.Vector3(e.x || e[0], e.y || e[1], e.z || e[2]));
                        hasBounds = true;
                    }
                });

                const bimElements = useBIMStore.getState().project.elements;
                for (const id of selectedIds) {
                    const el = bimElements.get(id);
                    if (!el) continue;
                    const bbox = computeElementsBBox([[id, el as any]]);
                    if (!isFinite(bbox.min[0])) continue;
                    bounds.expandByPoint(new THREE.Vector3(...bbox.min));
                    bounds.expandByPoint(new THREE.Vector3(...bbox.max));
                    hasBounds = true;
                }

                if (hasBounds && mainControls) {
                    const center = new THREE.Vector3();
                    bounds.getCenter(center);
                    mainControls.target.copy(center);
                    mainControls.update();
                } else {
                    await appAlert('Could not compute bounds for the selection.', { title: 'Lock to Selection' });
                }
            },
            description: 'Locks the view orbit center to the selected elements.'
        });



        registerCommand({
            id: 'viewCubeOptions',
            label: 'Options',
            category: 'View',
            action: () => {
                useAppStore.getState().toggleViewCubeOptions();
            },
            description: 'Opens ViewCube options.'
        });

        return () => {
            unregisterCommand('viewCubeGoHome');
            unregisterCommand('viewCubeToggleProjection');
            unregisterCommand('viewCubeSaveViewAsHome');
            unregisterCommand('viewCubeLockToSelection');

            unregisterCommand('viewCubeOptions');
        };
    }, [handleHome, updateView, registerCommand, unregisterCommand, mainControls, mainCamera]);

    const invalidate = useThree((state) => state.invalidate);

    useFrame(() => {
        if (targetPosition.current && targetQuaternion.current && mainCamera && mainControls) {
            // Demand render loop: request frames while the camera animation runs
            invalidate();
            const now = performance.now();
            const progress = Math.min((now - animationStartTime.current) / ANIMATION_DURATION, 1);
            const ease = 1 - Math.pow(1 - progress, 3);

            mainCamera.position.lerpVectors(startPosition.current, targetPosition.current, ease);
            mainCamera.quaternion.slerpQuaternions(startQuaternion.current, targetQuaternion.current, ease);

            if (progress >= 1) {
                targetPosition.current = null;
                targetQuaternion.current = null;
                mainControls.update();
            }
        }
    });

    // Compass Drag Logic
    const handleCompassDown = (e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();
        setViewCubeHoveredIfChanged(true);
        isDragging.current = true;

        previousMousePosition.current = { x: e.clientX, y: e.clientY };
        window.addEventListener('pointermove', handleCompassMove);
        window.addEventListener('pointerup', handleCompassUp);
    };

    const handleCompassMove = (e: PointerEvent) => {
        if (!isDragging.current || !mainCamera || !mainControls) return;

        const deltaX = e.clientX - previousMousePosition.current.x;
        previousMousePosition.current = { x: e.clientX, y: e.clientY };

        const speed = 0.005;
        let angle = -deltaX * speed;

        if (viewCubeOptions.inverseCompassRotation) {
            angle = -angle;
        }

        const target = mainControls.target;
        const pos = mainCamera.position.clone().sub(target);

        pos.applyAxisAngle(new THREE.Vector3(0, 0, 1), angle);
        mainCamera.position.copy(target.clone().add(pos));
        mainCamera.lookAt(target);
        mainControls.update();
        // Demand render loop: re-render after manual camera manipulation
        invalidate();
    };

    const handleCompassUp = () => {
        isDragging.current = false;
        window.removeEventListener('pointermove', handleCompassMove);
        window.removeEventListener('pointerup', handleCompassUp);

        // Snap to View Logic
        if (viewCubeOptions.snapToView && mainCamera && mainControls) {
            const target = mainControls.target;
            const offset = mainCamera.position.clone().sub(target);

            // Z-up Snapping Logic
            // Calculate azimuth in XY plane
            const currentTheta = Math.atan2(offset.y, offset.x);

            // Snap to nearest 90 degrees (PI/2)
            const snapAngle = Math.PI / 2;
            const snappedTheta = Math.round(currentTheta / snapAngle) * snapAngle;

            // Reconstruct position preserving XY radius and Z height
            const radiusXY = Math.sqrt(offset.x * offset.x + offset.y * offset.y);
            const newX = radiusXY * Math.cos(snappedTheta);
            const newY = radiusXY * Math.sin(snappedTheta);
            const newZ = offset.z;

            const newPos = new THREE.Vector3(newX, newY, newZ).add(target);

            // Preserve Scene Upright Logic
            // If preserveSceneUpright is true, we ensure the up vector is reset to Z-up (0,0,1)
            // Otherwise we use the camera's current up vector
            const newUp = viewCubeOptions.preserveSceneUpright ? new THREE.Vector3(0, 0, 1) : mainCamera.up.clone();

            animateCamera(newPos, newUp, target);
        }
    };

    return (
        <group scale={[baseScale, baseScale, baseScale]} onContextMenu={handleContextMenu}>
            {/* Division grid (light) + silhouette (dark): built once at module
                level, each segment exists exactly once and sits a hair off the
                shell surfaces, so nothing coincides or fights over depth. */}
            <lineSegments geometry={GRID_GEOMETRY} raycast={() => null}>
                <lineBasicMaterial color={EDGE_INNER_COLOR} transparent opacity={opacity} />
            </lineSegments>
            <lineSegments geometry={SILHOUETTE_GEOMETRY} raycast={() => null}>
                <lineBasicMaterial color={EDGE_COLOR} />
            </lineSegments>

            {/* Faces */}
            <Part name="TOP" label={t('viewport.cube.tOP')} position={[0, 0, FACE_OFFSET]} scale={[PANEL_SIZE, PANEL_SIZE, D]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'TOP'} type="face" opacity={opacity} />
            <Part name="BOTTOM" label={t('viewport.cube.bOTTOM')} position={[0, 0, -FACE_OFFSET]} scale={[PANEL_SIZE, PANEL_SIZE, D]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'BOTTOM'} type="face" opacity={opacity} />
            <Part name="FRONT" label={t('viewport.cube.fRONT')} position={[0, -FACE_OFFSET, 0]} scale={[PANEL_SIZE, D, PANEL_SIZE]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'FRONT'} type="face" opacity={opacity} />
            <Part name="BACK" label={t('viewport.cube.bACK')} position={[0, FACE_OFFSET, 0]} scale={[PANEL_SIZE, D, PANEL_SIZE]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'BACK'} type="face" opacity={opacity} />
            <Part name="LEFT" label={t('viewport.cube.lEFT')} position={[-FACE_OFFSET, 0, 0]} scale={[D, PANEL_SIZE, PANEL_SIZE]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'LEFT'} type="face" opacity={opacity} />
            <Part name="RIGHT" label={t('viewport.cube.rIGHT')} position={[FACE_OFFSET, 0, 0]} scale={[D, PANEL_SIZE, PANEL_SIZE]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'RIGHT'} type="face" opacity={opacity} />

            {/* Corners */}
            <Part name="TOP_FRONT_RIGHT" position={[CELL_OFFSET, -CELL_OFFSET, CELL_OFFSET]} scale={[C, C, C]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'TOP_FRONT_RIGHT'} type="corner" opacity={opacity} />
            <Part name="TOP_FRONT_LEFT" position={[-CELL_OFFSET, -CELL_OFFSET, CELL_OFFSET]} scale={[C, C, C]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'TOP_FRONT_LEFT'} type="corner" opacity={opacity} />
            <Part name="TOP_BACK_RIGHT" position={[CELL_OFFSET, CELL_OFFSET, CELL_OFFSET]} scale={[C, C, C]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'TOP_BACK_RIGHT'} type="corner" opacity={opacity} />
            <Part name="TOP_BACK_LEFT" position={[-CELL_OFFSET, CELL_OFFSET, CELL_OFFSET]} scale={[C, C, C]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'TOP_BACK_LEFT'} type="corner" opacity={opacity} />
            <Part name="BOTTOM_FRONT_RIGHT" position={[CELL_OFFSET, -CELL_OFFSET, -CELL_OFFSET]} scale={[C, C, C]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'BOTTOM_FRONT_RIGHT'} type="corner" opacity={opacity} />
            <Part name="BOTTOM_FRONT_LEFT" position={[-CELL_OFFSET, -CELL_OFFSET, -CELL_OFFSET]} scale={[C, C, C]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'BOTTOM_FRONT_LEFT'} type="corner" opacity={opacity} />
            <Part name="BOTTOM_BACK_RIGHT" position={[CELL_OFFSET, CELL_OFFSET, -CELL_OFFSET]} scale={[C, C, C]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'BOTTOM_BACK_RIGHT'} type="corner" opacity={opacity} />
            <Part name="BOTTOM_BACK_LEFT" position={[-CELL_OFFSET, CELL_OFFSET, -CELL_OFFSET]} scale={[C, C, C]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'BOTTOM_BACK_LEFT'} type="corner" opacity={opacity} />

            {/* Edges */}
            <Part name="TOP_FRONT" position={[0, -CELL_OFFSET, CELL_OFFSET]} scale={[PANEL_SIZE, C, C]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'TOP_FRONT'} type="edge" opacity={opacity} />
            <Part name="TOP_BACK" position={[0, CELL_OFFSET, CELL_OFFSET]} scale={[PANEL_SIZE, C, C]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'TOP_BACK'} type="edge" opacity={opacity} />
            <Part name="TOP_LEFT" position={[-CELL_OFFSET, 0, CELL_OFFSET]} scale={[C, PANEL_SIZE, C]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'TOP_LEFT'} type="edge" opacity={opacity} />
            <Part name="TOP_RIGHT" position={[CELL_OFFSET, 0, CELL_OFFSET]} scale={[C, PANEL_SIZE, C]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'TOP_RIGHT'} type="edge" opacity={opacity} />
            <Part name="BOTTOM_FRONT" position={[0, -CELL_OFFSET, -CELL_OFFSET]} scale={[PANEL_SIZE, C, C]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'BOTTOM_FRONT'} type="edge" opacity={opacity} />
            <Part name="BOTTOM_BACK" position={[0, CELL_OFFSET, -CELL_OFFSET]} scale={[PANEL_SIZE, C, C]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'BOTTOM_BACK'} type="edge" opacity={opacity} />
            <Part name="BOTTOM_LEFT" position={[-CELL_OFFSET, 0, -CELL_OFFSET]} scale={[C, PANEL_SIZE, C]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'BOTTOM_LEFT'} type="edge" opacity={opacity} />
            <Part name="BOTTOM_RIGHT" position={[CELL_OFFSET, 0, -CELL_OFFSET]} scale={[C, PANEL_SIZE, C]} onHover={handleHover} onClick={handleClick} hovered={hoveredPart === 'BOTTOM_RIGHT'} type="edge" opacity={opacity} />

            {/* Compass Ring */}
            {viewCubeOptions.showCompass && (
                <group rotation={[0, 0, 0]} position={[0, 0, -0.6]}>
                    <mesh onPointerDown={handleCompassDown}
                        onPointerOver={() => { document.body.style.cursor = 'grab'; setViewCubeHoveredIfChanged(true); }}
                        onPointerOut={() => { document.body.style.cursor = 'auto'; setViewCubeHoveredIfChanged(false); }}>
                        <ringGeometry args={[1.1, 1.4, 64]} />
                        <meshBasicMaterial color="#e0e0e0" side={THREE.DoubleSide} transparent opacity={opacity} />
                    </mesh>
                    {/* Letters lifted 0.01 off the ring plane: coplanar text
                        quads and the ring surface fight over depth. */}
                    <Text position={[0, 1.25, 0.01]} fontSize={0.2} color="black" rotation={[0, 0, 0]} fillOpacity={opacity}>N</Text>
                    <Text position={[0, -1.25, 0.01]} fontSize={0.2} color="black" rotation={[0, 0, Math.PI]} fillOpacity={opacity}>S</Text>
                    <Text position={[1.25, 0, 0.01]} fontSize={0.2} color="black" rotation={[0, 0, -Math.PI / 2]} fillOpacity={opacity}>E</Text>
                    <Text position={[-1.25, 0, 0.01]} fontSize={0.2} color="black" rotation={[0, 0, Math.PI / 2]} fillOpacity={opacity}>W</Text>
                </group>
            )}

            {/* Home Button */}
            <HomeButton onClick={handleHome} hovered={homeHovered} onHover={(v) => { setHomeHovered(v); setViewCubeHoveredIfChanged(v); }} opacity={opacity} />
        </group>
    );
};

export const WorkbenchViewCube = memo(WorkbenchViewCubeComponent);
